import React, { useState } from 'react';
import { ArrowRight, Code, TrendingUp, Sparkles, ShieldCheck, Zap, Globe, MessageCircle, Server, CheckCircle2, ChevronLeft, ChevronRight, Star, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ClientGrid } from '../components/ClientGrid';
import { ClientLogo } from '../components/ClientLogo';
import { ALL_CLIENTS } from '../data/clients';
import { Seo } from '../components/Seo';

export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      quote: "Swaroop Web Solutions overhauled our web ecosystem and implemented Generative Engine Optimization. We immediately started appearing in ChatGPT and Perplexity recommendations for enterprise search terms.",
      author: "Rajesh Kulkarni",
      role: "Founder & CEO, CloudPeak Pune",
      rating: 5,
      initials: "RK"
    },
    {
      quote: "Their high-performance engineering delivered an ultra-fast website scoring 100/100 across Google Core Web Vitals. Our organic conversion rate doubled within 60 days.",
      author: "Priya Menon",
      role: "Director of Digital, Horizon Media",
      rating: 5,
      initials: "PM"
    },
    {
      quote: "The lead capture pipeline with instant automated email dispatches and 1-click WhatsApp alerts ensures our sales team engages inquiries within minutes. Outstanding technical execution!",
      author: "David Vance",
      role: "VP Growth, Apex Global",
      rating: 5,
      initials: "DV"
    }
  ];

  const featuredProjects = [
    {
      title: "Charles Tyrwhitt",
      domain: "charlestyrwhitt.com",
      category: "E-Commerce Architecture & Performance",
      image: "/images/charles-tyrwhitt.webp",
      highlight: "Global E-Commerce optimization & sub-second render speeds"
    },
    {
      title: "Brilliant Trade Australia",
      domain: "brillianttrade.com.au",
      category: "B2B Enterprise Portal",
      image: "/images/brilliant-trade.webp",
      highlight: "High-volume commercial catalog & product discovery engine"
    },
    {
      title: "Mayfield Lighting",
      domain: "mayfieldlighting.com.au",
      category: "Brand Showcase & Design System",
      image: "/images/mayfield-lighting.webp",
      highlight: "Accessible, high-fidelity UI/UX with fluid responsive interactions"
    }
  ];

  return (
    <div className="pt-6 font-sans">
      <Seo title="Web Development, SEO & AI-SEO Services | Swaroop Web Solutions" description="Swaroop Web Solutions provides high-performance website design, web development, technical SEO, AI-SEO, social media marketing, e-commerce and website AMC services in Pune and worldwide." path="/" keywords="web development, website design, technical SEO, AI-SEO, GEO, e-commerce development, website AMC, Pune web developer" />
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-18 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center" aria-labelledby="hero-heading">
        <div className="lg:col-span-7 flex flex-col gap-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0059bb]/10 border border-[#0059bb]/20 text-[#0059bb] text-xs font-bold uppercase tracking-wider w-fit">
            <Sparkles size={14} className="text-[#0059bb]" />
            <span>High-Performance Web &amp; AI-SEO Agency</span>
          </div>
          <div className="eyebrow w-fit bg-amber-50 text-amber-800 border border-amber-200">
            <Star size={14} fill="currentColor" aria-hidden="true" />
            <span>Award-Winning Developer</span>
          </div>

          <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-[2.9rem] font-display font-extrabold text-[#0b1c30] leading-[1.15] tracking-tight">
            Engineering High-Performance Web &amp; <span className="text-[#0059bb]">AI Search Authority.</span>
          </h1>

          <p className="text-base sm:text-lg text-gray-600 max-w-2xl leading-relaxed font-normal">
            We engineer lightning-fast custom web platforms, bespoke UI/UX design systems, and Generative Engine Optimization (GEO) so your business dominates organic search engines and AI discovery tools.
          </p>

          <div className="flex flex-wrap gap-3.5 pt-1">
            <Link 
              to="/contact" 
              className="bg-[#0059bb] hover:bg-[#0070ea] text-white px-7 py-3.5 rounded-xl font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all shadow-md flex items-center gap-2"
            >
              <span>Get Free Proposal</span>
              <ArrowRight size={16} />
            </Link>
            
            <a 
              href="https://api.whatsapp.com/send?phone=919175931452&text=Hi%20Swaroop%20Web%20Solutions,%20I%20would%20like%20to%20consult%20about%20web%20engineering%20and%20AI-SEO."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#075e2f] hover:bg-[#064b26] text-white px-6 sm:px-7 py-3.5 rounded-xl font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all shadow-md flex items-center gap-2 min-h-11"
            >
              <MessageCircle size={16} />
              <span>WhatsApp Us</span>
            </a>

            <Link 
              to="/services" 
              className="border border-gray-300 hover:border-[#0059bb] text-gray-800 hover:text-[#0059bb] px-6 py-3.5 rounded-xl font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all bg-white shadow-2xs"
            >
              View Capabilities
            </Link>
          </div>

          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
            <div>
              <div className="text-2xl sm:text-3xl font-display font-extrabold text-[#0059bb]">100/100</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Core Web Vitals</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-display font-extrabold text-[#0b1c30]">&lt; 0.4s</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Page Load Speed</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-display font-extrabold text-emerald-700">WCAG AA</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">ADA Compliant UI</div>
            </div>
          </div>
        </div>

        {/* Visual Showcase Card */}
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-200 bg-[#0b1c30]">
            <img 
              src="/images/hero.webp"
              alt="High performance web engineering and analytics dashboard by Swaroop Web Solutions" 
              width={1200}
              height={800}
              loading="eager"
              fetchPriority="high"
              srcSet="/images/hero.webp 640w, /images/hero-large.webp 960w"
              sizes="(max-width: 1024px) calc(100vw - 2rem), 42vw"
              decoding="async"
              className="w-full h-80 sm:h-96 object-cover opacity-60 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b1c30] via-[#0b1c30]/50 to-transparent p-6 sm:p-8 flex flex-col justify-end">
              <div className="space-y-2.5">
                <div className="inline-flex items-center gap-1.5 bg-[#0059bb] text-white px-3 py-1 rounded-full text-xs font-bold shadow-xs">
                  <ShieldCheck size={14} /> Enterprise Web Engineering
                </div>
                <h2 className="text-white font-display font-bold text-lg sm:text-xl leading-snug">
                  High-Speed Architecture &amp; Instant Lead Ingestion
                </h2>
                <p className="text-gray-200 text-xs sm:text-sm leading-relaxed">
                  Automated routing directly to Admin with instant WhatsApp notifications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Logos / Trust Ecosystem */}
      <section className="bg-white border-y border-gray-200 py-12" aria-label="Trusted Enterprise Portals">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#0059bb]">
                Enterprise Client Ecosystem
              </span>
              <h2 className="text-xl sm:text-2xl font-display font-bold text-[#0b1c30]">
                Trusted by Global Brands &amp; Portals
              </h2>
            </div>
            <Link to="/portfolio" className="text-xs sm:text-sm font-semibold text-[#0059bb] hover:underline flex items-center gap-1 shrink-0">
              <span>View All Client Portals</span>
              <ArrowRight size={14} />
            </Link>
          </div>
          
          {/* 12 Client Logos Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {ALL_CLIENTS.map((client) => (
              <div key={client.id} className="min-w-0">
                <ClientLogo client={client} showDetails={false} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Capabilities Section */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 space-y-12" aria-labelledby="capabilities-heading">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="text-xs font-bold uppercase tracking-wider text-[#0059bb]">Our Core Capabilities</span>
          <h2 id="capabilities-heading" className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-[#0b1c30]">
            Website Design, Marketing &amp; AMC
          </h2>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
            From bespoke design systems and custom web engineering to aggressive SEO, Social Media Marketing, and reliable Website Annual Maintenance Contracts (AMC).
          </p>
        </div>

        {/* Featured AMC Callout Banner */}
        <div className="bg-[#0b1c30] text-white p-6 sm:p-8 rounded-3xl border border-blue-900/50 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 shadow-md">
          <div className="space-y-2 text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0059bb]/30 text-blue-300 text-xs font-bold uppercase tracking-wider">
              <ShieldCheck size={14} className="text-blue-400" />
              <span>Website Annual Maintenance Contract (AMC)</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-display font-bold text-white">Comprehensive Website Maintenance &amp; Cloud Hosting</h3>
            <p className="text-sm text-gray-300 max-w-2xl leading-relaxed">
              We can maintain your website to ensure that it is always up and running. We will perform regular backups, security scans, and software updates.
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 bg-[#0059bb] hover:bg-[#0070ea] text-white px-6 py-3.5 rounded-xl font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all shadow-md"
          >
            Enroll in Website AMC
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1: Website Design */}
          <div className="bg-white border border-gray-200 p-6 sm:p-7 rounded-3xl space-y-4 hover:border-[#0059bb] hover:shadow-lg transition-all flex flex-col justify-between">
            <div className="space-y-3">
              <div className="w-11 h-11 bg-pink-100 text-pink-700 rounded-xl flex items-center justify-center font-bold text-lg">
                🎨
              </div>
              <h3 className="text-lg sm:text-xl font-display font-bold text-[#0b1c30]">Website Design</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Bespoke user interface and design systems crafted for visual prestige, brand trust, and responsive mobile journeys.
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700 pt-2 border-t border-gray-100 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#0059bb] shrink-0" />
                  <span>Modern UI/UX design tokens</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#0059bb] shrink-0" />
                  <span>WCAG AA accessibility</span>
                </li>
              </ul>
            </div>
            <div className="pt-2">
              <Link to="/services" className="text-xs sm:text-sm font-semibold text-[#0059bb] uppercase tracking-wider flex items-center gap-1 hover:underline">
                <span>View Details</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Card 2: Website Development */}
          <div className="bg-white border border-gray-200 p-6 sm:p-7 rounded-3xl space-y-4 hover:border-[#0059bb] hover:shadow-lg transition-all flex flex-col justify-between">
            <div className="space-y-3">
              <div className="w-11 h-11 bg-blue-100 text-[#0059bb] rounded-xl flex items-center justify-center">
                <Code size={22} />
              </div>
              <h3 className="text-lg sm:text-xl font-display font-bold text-[#0b1c30]">Website Development</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Custom web architectures, portals, and headless systems engineered for sub-second speed, zero bloat, and stability.
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700 pt-2 border-t border-gray-100 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#0059bb] shrink-0" />
                  <span>100/100 Core Web Vitals</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#0059bb] shrink-0" />
                  <span>Sub-second load times</span>
                </li>
              </ul>
            </div>
            <div className="pt-2">
              <Link to="/services" className="text-xs sm:text-sm font-semibold text-[#0059bb] uppercase tracking-wider flex items-center gap-1 hover:underline">
                <span>View Details</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Card 3: SEO */}
          <div className="bg-white border border-gray-200 p-6 sm:p-7 rounded-3xl space-y-4 hover:border-[#0059bb] hover:shadow-lg transition-all flex flex-col justify-between">
            <div className="space-y-3">
              <div className="w-11 h-11 bg-purple-100 text-purple-700 rounded-xl flex items-center justify-center">
                <Sparkles size={22} />
              </div>
              <h3 className="text-lg sm:text-xl font-display font-bold text-[#0b1c30]">SEO &amp; AI-SEO</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Generative Engine Optimization &amp; technical search strategies for Google, ChatGPT, Claude, and Gemini citations.
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700 pt-2 border-t border-gray-100 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-purple-600 shrink-0" />
                  <span>JSON-LD Knowledge Graph</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-purple-600 shrink-0" />
                  <span>Organic search dominance</span>
                </li>
              </ul>
            </div>
            <div className="pt-2">
              <Link to="/services" className="text-xs sm:text-sm font-semibold text-[#0059bb] uppercase tracking-wider flex items-center gap-1 hover:underline">
                <span>View Details</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Card 4: Social Media Marketing */}
          <div className="bg-white border border-gray-200 p-6 sm:p-7 rounded-3xl space-y-4 hover:border-[#0059bb] hover:shadow-lg transition-all flex flex-col justify-between">
            <div className="space-y-3">
              <div className="w-11 h-11 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center">
                <TrendingUp size={22} />
              </div>
              <h3 className="text-lg sm:text-xl font-display font-bold text-[#0b1c30]">Social Media Marketing</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Multi-channel brand awareness campaigns, content marketing, and high-converting audience acquisition funnels.
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700 pt-2 border-t border-gray-100 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-700 shrink-0" />
                  <span>Targeted audience growth</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-700 shrink-0" />
                  <span>Measurable lead conversion</span>
                </li>
              </ul>
            </div>
            <div className="pt-2">
              <Link to="/services" className="text-xs sm:text-sm font-semibold text-[#0059bb] uppercase tracking-wider flex items-center gap-1 hover:underline">
                <span>View Details</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects Showcase */}
      <section className="bg-white py-20 border-y border-gray-200" aria-labelledby="projects-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#0059bb]">Selected Case Studies</span>
              <h2 id="projects-heading" className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-[#0b1c30] mt-1">
                Featured Engineering Work
              </h2>
            </div>
            <Link 
              to="/portfolio" 
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#0059bb] hover:text-[#0b1c30] transition-colors"
            >
              <span>View All Projects</span>
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {featuredProjects.map((project, idx) => (
              <div 
                key={idx} 
                className="group rounded-3xl overflow-hidden border border-gray-200 bg-white hover:shadow-xl transition-all flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={`${project.title} - ${project.category} web engineering case study`}
                    width={800}
                    height={500}
                    loading="lazy"
                    decoding="async"
                    srcSet={`${project.image} 640w`}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#0b1c30]/90 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                      {project.category}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between space-y-4">
                  <div>
                    <h3 className="text-lg sm:text-xl font-display font-bold text-[#0b1c30] mb-1">{project.title}</h3>
                    <p className="text-xs text-gray-500 font-mono mb-2">{project.domain}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{project.highlight}</p>
                  </div>
                  <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                    <Link 
                      to="/contact" 
                      aria-label={`Discuss project scope for ${project.title}`}
                      className="text-xs sm:text-sm font-bold text-[#0059bb] hover:underline flex items-center gap-1 min-h-[36px] py-1"
                    >
                      <span>Discuss Scope</span>
                      <ArrowRight size={14} />
                    </Link>
                    <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 size={13} /> High Performance
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Complete Client Ecosystem & Directory Section */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6" aria-labelledby="clients-directory-heading">
        <ClientGrid 
          title="Enterprise Portals &amp; Client Directory"
          subtitle="Explore all live platforms, e-commerce storefronts, and brand networks engineered under our high-performance technical stack."
          showFullDirectory={true}
        />
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6" aria-labelledby="testimonials-heading">
        <div className="bg-[#0b1c30] text-white rounded-3xl p-8 sm:p-14 relative overflow-hidden shadow-xl">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-1 text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} fill="currentColor" />
              ))}
            </div>

            <div className="min-h-[130px] flex items-center justify-center">
              <blockquote
                className="text-lg sm:text-xl md:text-2xl font-display font-normal text-gray-100 leading-relaxed italic"
              >
                "{testimonials[activeTestimonial].quote}"
              </blockquote>
            </div>

            <div className="space-y-1">
              <div className="font-display font-bold text-base sm:text-lg text-white">
                {testimonials[activeTestimonial].author}
              </div>
              <div className="text-xs sm:text-sm text-[#38bdf8] font-medium">
                {testimonials[activeTestimonial].role}
              </div>
            </div>

            <div className="flex justify-center items-center gap-3 pt-4">
              <button
                onClick={() => setActiveTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                className="w-10 h-10 rounded-full bg-[#132742] hover:bg-[#1e3a5f] text-white flex items-center justify-center transition-colors border border-[#233e66]"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex gap-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className="w-11 h-11 rounded-full transition-all flex items-center justify-center"
                    aria-label={`Go to testimonial ${idx + 1}`}
                  >
                    <span
                      className={`block h-2.5 rounded-full transition-all ${
                        activeTestimonial === idx ? 'w-8 bg-[#0059bb]' : 'w-2.5 bg-gray-600'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <button
                onClick={() => setActiveTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                className="w-10 h-10 rounded-full bg-[#132742] hover:bg-[#1e3a5f] text-white flex items-center justify-center transition-colors border border-[#233e66]"
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" aria-label="Call to Action">
        <div className="bg-gradient-to-br from-[#0059bb] to-[#0b1c30] text-white rounded-3xl p-8 sm:p-14 relative overflow-hidden shadow-2xl">
          <div className="max-w-2xl space-y-6 relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/15 text-white px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-xs">
              <Zap size={14} className="text-amber-300" /> Direct Technical Consultation
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold leading-tight text-white">
              Ready to Upgrade Your Web Performance &amp; AI Search Visibility?
            </h2>
            <p className="text-blue-100 text-sm sm:text-base leading-relaxed">
              Partner with Swaroop Web Solutions for custom engineering, sub-second loading speeds, and Generative Engine Optimization.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link 
                to="/contact" 
                className="bg-white hover:bg-gray-100 text-[#0b1c30] px-7 py-3.5 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wide transition-all shadow-md"
              >
                Request Free Quote
              </Link>
              <a 
                href="https://api.whatsapp.com/send?phone=919175931452&text=Hello%20Swaroop%20Web%20Solutions,%20I%20am%20ready%20to%20discuss%20my%20project." 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#075e2f] hover:bg-[#064b26] text-white px-6 sm:px-7 py-3.5 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wide transition-all flex items-center gap-2 shadow-md"
              >
                <MessageCircle size={16} />
                <span>WhatsApp: +91 91759 31452</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
